// stores/useReviewsStore.ts
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { PendingReviewRepositoryFetch } from '../apps/user-reviews/pending-review/PendingReviewRepositoryFetch'
import { PendingReview } from '../apps/user-reviews/pending-review/schemas'

interface RequestedCampsiteReview {
  slug: string
  campsiteName: string
}

interface ReviewsData {
  pendingReviews: PendingReview[]
  requestedCampsiteReview: RequestedCampsiteReview | null
}

export const useReviewsStore = defineStore('useReviewsStore', () => {
  const _reviewsData = useStorage<ReviewsData>('reviews-data', {
    pendingReviews: [],
    requestedCampsiteReview: null,
  })

  const authStore = useAuthStore(usePinia())
  const pendingReviewRepository = new PendingReviewRepositoryFetch()

  if (process.client) {
    // Initial fetch if user is already logged in
    if (authStore.isLoggedIn) {
      void _fetchPendingReviews()
    }

    watch(
      () => authStore.isLoggedIn,
      async (value, oldValue) => {
        if (value && value !== oldValue) {
          await _fetchPendingReviews()
        }
        if (!value && value !== oldValue) {
          _clearReviewsData()
        }
      },
    )
  }

  const pendingReviews = computed(() => {
    return _reviewsData.value.pendingReviews || []
  })

  const requestedCampsiteReview = computed(() => {
    return _reviewsData.value.requestedCampsiteReview
  })

  function _clearReviewsData() {
    _reviewsData.value.pendingReviews = []
  }

  async function _fetchPendingReviews() {
    try {
      const { locale } = useI18n().i18n
      const langCode = locale || 'en-gb'
      const reviews = await pendingReviewRepository.getPending(langCode)
      _reviewsData.value.pendingReviews = reviews
    } catch (error) {
      console.error('Failed to fetch pending reviews:', error)
      _reviewsData.value.pendingReviews = []
    }
  }

  async function refreshPendingReviews() {
    if (authStore.isLoggedIn) {
      await _fetchPendingReviews()
    }
  }

  function setRequestedCampsiteReview(slug: string, campsiteName: string) {
    _reviewsData.value.requestedCampsiteReview = {
      slug,
      campsiteName,
    }
  }

  function clearRequestedCampsiteReview() {
    _reviewsData.value.requestedCampsiteReview = null
  }

  return {
    clearRequestedCampsiteReview,
    pendingReviews,
    refreshPendingReviews,
    requestedCampsiteReview,
    setRequestedCampsiteReview,
  }
})
